(ns minorodata.components.warehouse-usage.query-diagnosis
  (:require
   ["date-fns" :refer [format parseISO]]
   ["lucide-react" :refer [Snowflake]]
   [minorodata.components.home-dashboard.shared :refer [chart-colors mini-stat]]
   [minorodata.components.warehouse-usage.shared :refer [bucket-date-key bucket-total-key
                                                          bucketed-series chart-card
                                                          chart-tick-class dimension-label
                                                          ellipsize
                                                          empty-state format-bytes format-integer
                                                          format-number state-card timeframe]]
   [minorodata.components.spinner.core :as spinner]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [recharts :refer [Bar BarChart CartesianGrid LabelList ReferenceLine Scatter
                     ScatterChart Tooltip XAxis YAxis]]
   [ui.fields :as fields]
   [ui.table :as table]
   [ui.typography :as typography]))

(defn- iso-date
  [date]
  (format date "yyyy-MM-dd"))

(defonce ^:private filter-query-type (reagent/atom ""))
(defonce ^:private filter-warehouse (reagent/atom ""))
(defonce ^:private filter-user (reagent/atom ""))
(defonce ^:private limit-selection (reagent/atom "50"))

(defn- diagnosis-params []
  (let [{:keys [from to]} @timeframe]
    {:from       (iso-date from)
     :to         (iso-date to)
     :query-type (not-empty @filter-query-type)
     :warehouse  (not-empty @filter-warehouse)
     :user       (not-empty @filter-user)
     :limit      (js/parseInt @limit-selection 10)}))

(defn refresh!
  []
  (re-frame/dispatch [:usage-account.events/init-diagnosis (diagnosis-params)]))

(defn- set-query-type-filter! [value]
  (reset! filter-query-type value)
  (re-frame/dispatch [:usage-account.events/refresh-diagnosis-query-type (diagnosis-params)]))

(defn- set-dimension-filter! [atom-ref value]
  (reset! atom-ref value)
  (re-frame/dispatch [:usage-account.events/refresh-diagnosis-dimension (diagnosis-params)]))

(defn- set-limit! [value]
  (reset! limit-selection value)
  (re-frame/dispatch [:usage-account.events/refresh-diagnosis-limit (diagnosis-params)]))

(defn- dimension-options [filter-options dimension all-label]
  (into [{:value "" :text all-label}]
        (->> filter-options
             (filter #(= dimension (:dimension %)))
             (keep :value)
             distinct
             sort
             (map (fn [v] {:value v :text v})))))

(def ^:private limit-options
  [{:value "10" :text "Top 10"}
   {:value "25" :text "Top 25"}
   {:value "50" :text "Top 50"}
   {:value "100" :text "Top 100"}
   {:value "200" :text "Top 200"}])

(defn- filters-row [filter-options disabled?]
  [:div {:class "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,1fr))_10rem]"}
   [fields/select {:value      @filter-query-type
                   :on-change  (fn [e] (set-query-type-filter! (.. e -target -value)))
                   :options    (dimension-options filter-options "query-type" "All query types")
                   :disabled   disabled?
                   :class      "min-w-0"}]
   [fields/select {:value      @filter-warehouse
                   :on-change  (fn [e] (set-dimension-filter! filter-warehouse (.. e -target -value)))
                   :options    (dimension-options filter-options "warehouse" "All warehouses")
                   :disabled   disabled?
                   :class      "min-w-0"}]
   [fields/select {:value      @filter-user
                   :on-change  (fn [e] (set-dimension-filter! filter-user (.. e -target -value)))
                   :options    (dimension-options filter-options "user" "All users")
                   :disabled   disabled?
                   :class      "min-w-0"}]
   [fields/select {:value      @limit-selection
                   :on-change  (fn [e] (set-limit! (.. e -target -value)))
                   :options    limit-options
                   :disabled   disabled?}]])

(defn- format-hours [ms]
  (if (nil? ms) "—" (str (.toFixed (/ ms 3600000) 1) "h")))

(defn- summary-row [summary loading?]
  [:div {:class "grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6"}
   [mini-stat {:title "Executions" :value (format-integer (:executions summary)) :loading loading?}]
   [mini-stat {:title "Query Shapes" :value (format-integer (:shapes summary)) :loading loading?}]
   [mini-stat {:title "Users" :value (format-integer (:users summary)) :loading loading?}]
   [mini-stat {:title "Credits Burned" :value (format-number (:total-credits summary)) :loading loading?}]
   [mini-stat {:title "Compute Hours" :value (format-hours (:total-elapsed-ms summary)) :loading loading?}]
   [mini-stat {:title "Bytes Scanned" :value (format-bytes (:bytes-scanned summary)) :loading loading?}]])

(defn- median [nums]
  (when (seq nums)
    (let [sorted (vec (sort nums))
          n      (count sorted)
          mid    (quot n 2)]
      (if (odd? n)
        (nth sorted mid)
        (/ (+ (nth sorted (dec mid)) (nth sorted mid)) 2)))))

(defn- quadrant-chart [shapes loading?]
  [chart-card
   "Frequency vs Cost"
   nil
   (cond
     loading?
     [:div {:class "flex h-[300px] w-full items-center justify-center"}
      [spinner/with-text {:text "Loading frequency vs cost..."}]]

     (empty? shapes)
     [empty-state Snowflake "No query shapes for the current filters."]

     :else
     (let [median-x (median (keep :executions shapes))
           median-y (median (keep :total-credits shapes))]
       [:> ScatterChart {:width "100%" :height 300 :margin #js {:top 20 :right 20 :bottom 20 :left 0}}
        [:> CartesianGrid {}]
        [:> XAxis {:type "number" :dataKey "executions" :name "Executions"}]
        [:> YAxis {:type "number" :dataKey "total-credits" :name "Credits Burned"}]
        [:> Tooltip {:cursor {:strokeDasharray "3 3"}}]
        (when median-x [:> ReferenceLine {:x median-x :stroke "var(--color-kleene-text-secondary)" :stroke-dasharray "3 3"}])
        (when median-y [:> ReferenceLine {:y median-y :stroke "var(--color-kleene-text-secondary)" :stroke-dasharray "3 3"}])
        [:> Scatter {:data shapes :fill (first chart-colors)}]]))])

(defn- type-series-chart [rows loading?]
  (let [{:keys [rows series]} (bucketed-series rows :query-type :total-credits)]
    [chart-card
     "Credits by Query Type"
     nil
     (cond
       loading?
       [:div {:class "flex h-[300px] w-full items-center justify-center"}
        [spinner/with-text {:text "Loading credits by query type..."}]]

       (empty? rows)
       [empty-state Snowflake "No query type breakdown for the current filters."]

       :else
       [:div {:class "flex min-w-0 gap-6"}
        [:div {:class "min-w-0 flex-1"}
         [:> BarChart {:width "100%" :height 300 :data rows :margin #js {:top 20 :bottom 20}}
          [:> CartesianGrid {:vertical false}]
          [:> XAxis {:dataKey bucket-date-key
                     :axis-line false
                     :angle -30
                     :height 55
                     :text-anchor "end"
                     :tick {:class chart-tick-class}
                     :tick-formatter #(format (parseISO %) "d MMM")}]
          [:> YAxis {:type "number" :axis-line false :tick-line false :tick {:class chart-tick-class}}]
          [:> Tooltip {:label-formatter #(format (parseISO %) "d MMM yyyy")}]
          (doall
           (for [[index query-type] (map-indexed vector series)]
             [:> Bar {:key query-type
                      :dataKey query-type
                      :name (dimension-label query-type)
                      :stackId "credits"
                      :fill (nth chart-colors index "var(--color-slate-500)")}
              (when (= index (dec (count series)))
                [:> LabelList {:dataKey bucket-total-key :position "top" :formatter format-number}])]))]]
        [:div {:class "w-44 shrink-0 self-center max-h-[300px] overflow-y-auto pr-1"}
         [typography/h4 {:class "mb-3"} "Query Type"]
         [:div {:class "flex flex-col gap-2"}
          (doall
           (for [[index query-type] (map-indexed vector series)]
             ^{:key query-type}
             [:div {:class "flex min-w-0 items-center gap-2"}
              [:span {:class "h-2.5 w-2.5 shrink-0 rounded-sm"
                      :style {:background-color (nth chart-colors index "var(--color-slate-500)")}}]
              [:span {:class (str "min-w-0 truncate " chart-tick-class)
                      :title (dimension-label query-type)}
               (ellipsize (dimension-label query-type) 22)]]))]]])]))

(defn- format-date [iso-str]
  (if iso-str (format (parseISO iso-str) "d MMM yyyy") "—"))

(defn- query-type-cell [row]
  (let [extra (dec (or (:query-types row) 1))]
    (str (:query-type row) (when (pos? extra) (str " (+" extra ")")))))

(defn- shapes-table [rows loading?]
  [chart-card
   "Higher Cost Queries"
   nil
   (cond
     loading?
     [:div {:class "flex h-48 w-full items-center justify-center"}
      [spinner/with-text {:text "Loading higher cost queries..."}]]

     (empty? rows)
     [empty-state Snowflake "No query shapes for the current filters."]

     :else
     [:div {:class "max-h-[450px] overflow-x-auto overflow-y-auto"}
      [table/table {:class "min-w-full" :sticky-header true}
       [table/thead
        [table/tr {:sticky true :class "*:top-0!"}
         [table/th "Query shape"]
         [table/th "Query type"]
         [table/th "Executions"]
         [table/th "Users"]
         [table/th "Warehouses"]
         [table/th "Credits burned"]
         [table/th "Credits / execution"]
         [table/th "Compute hours"]
         [table/th "Bytes scanned"]
         [table/th "Bytes spilled"]
         [table/th "Last seen"]]]
       [table/tbody
        (for [row rows]
          ^{:key (:query-shape row)}
          [table/tr
           [table/td {:title (:query-shape row)} (ellipsize (:query-shape row) 60)]
           [table/td {:class "whitespace-nowrap"} (query-type-cell row)]
           [table/td {:class "whitespace-nowrap"} (format-integer (:executions row))]
           [table/td {:class "whitespace-nowrap"} (format-integer (:users row))]
           [table/td {:class "whitespace-nowrap"} (format-integer (:warehouses row))]
           [table/td {:class "whitespace-nowrap"} (format-number (:total-credits row))]
           [table/td {:class "whitespace-nowrap"} (format-number (:credits-per-execution row))]
           [table/td {:class "whitespace-nowrap"} (format-hours (:total-elapsed-ms row))]
           [table/td {:class "whitespace-nowrap"} (format-bytes (:bytes-scanned row))]
           [table/td {:class "whitespace-nowrap"} (format-bytes (:bytes-spilled row))]
           [table/td {:class "whitespace-nowrap"} (format-date (:last-seen row))]])]]])])

(defn- summary-section [status summary]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Higher Cost Queries diagnosis isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Higher Cost Queries data."]]
    :loaded           [summary-row summary false]
    [summary-row summary true]))

(defn- quadrant-section [status shapes]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Higher Cost Queries diagnosis isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Higher Cost Queries data."]]
    :loaded           [quadrant-chart shapes false]
    [quadrant-chart shapes true]))

(defn- type-series-section [status rows]
  (case status
    :not-provisioned [state-card [empty-state Snowflake "Higher Cost Queries diagnosis isn't available for your account."]]
    :error            [state-card [empty-state Snowflake "Failed to load Higher Cost Queries data."]]
    :loaded           [type-series-chart rows false]
    [type-series-chart rows true]))

(defn- shapes-table-section [status shapes]
  (case status
    :not-provisioned nil
    :error            nil
    :loaded           [shapes-table shapes false]
    [shapes-table shapes true]))

(defn section []
  (let [filter-options-status @(re-frame/subscribe [:usage-account.subs/filter-options-status])
        filter-options        @(re-frame/subscribe [:usage-account.subs/filter-options])
        summary-status        @(re-frame/subscribe [:usage-account.subs/shape-summary-status])
        summary               @(re-frame/subscribe [:usage-account.subs/shape-summary])
        shapes-status         @(re-frame/subscribe [:usage-account.subs/shapes-status])
        shapes                @(re-frame/subscribe [:usage-account.subs/shapes])
        type-series-status    @(re-frame/subscribe [:usage-account.subs/query-type-series-status])
        type-series           @(re-frame/subscribe [:usage-account.subs/query-type-series])]
    [:div {:class "flex flex-col gap-6"}
     [filters-row filter-options (= filter-options-status :not-provisioned)]
     [summary-section summary-status summary]
     [:div {:class "grid grid-cols-1 gap-6 xl:grid-cols-2"}
      [quadrant-section shapes-status shapes]
      [type-series-section type-series-status type-series]]
     [shapes-table-section shapes-status shapes]]))
